/**
 * Cadence — free-form interval + unit, with `'off'` as a first-class paused value.
 *
 * Used by:
 *   - `BrandAuditConfig.cadenceOverride[axis]` (per-brand × per-axis user setting)
 *   - `BrandSchedule.cadence` (cache of the value at last sync, on the brand row)
 *   - `EmailRecipient` (per-recipient digest cadence — added in S5)
 *
 * Design rationale: docs/scheduler-redesign-phases-s2-s6.md §0.2 D-2.
 */
export type Cadence =
  | { interval: number; unit: 'days' | 'weeks' | 'months' }
  | 'off';

/**
 * Audit types that can be scheduled (= every type with a cadence row in
 * `BrandAuditConfig.cadenceOverride`).
 *
 * `brand` (legacy combined type) and `social` are NOT included — `brand` is
 * read-only for historical audits and `social` is not yet implemented. Both
 * can be added later by extending this tuple + tier defaults below.
 */
// NOTE: `news_watch` is deliberately NOT here. "Studio de Création" is a PULL
// feature gated by the monthly `content_creations` quota (see metering.ts) — it
// has NO automatic cadence. It was removed from the schedulable set 2026-07-05;
// `syncBrand` prunes any pre-existing news_watch EventBridge schedule.
export const SCHEDULABLE_AUDIT_TYPES = [
  'website',
  'earned_media',
  'brand_narrative',
  'brand_authority',
  'brand_referral',
] as const;
export type SchedulableAuditType = (typeof SCHEDULABLE_AUDIT_TYPES)[number];

export function isSchedulableAuditType(t: string): t is SchedulableAuditType {
  return (SCHEDULABLE_AUDIT_TYPES as readonly string[]).includes(t);
}

/**
 * Tier defaults — applied when `brand.auditConfig.cadenceOverride[axis]` is unset.
 *
 * Numbers from docs/billing/pricing-and-billing.md §2.4 (the table is the source
 * of truth). Editing here without updating the doc is a bug.
 */
export const CADENCE_DEFAULTS: Record<string, Record<SchedulableAuditType, Cadence>> = {
  // Basic = monthly on the heavy axes (website fan-out + earned media + news
  // watch); pro/elite run those weekly. Self-serve runs are compute only (no
  // credit spend), so cadence is the throughput differentiator. The single
  // `website` cadence drives the whole Phase-1 fan-out (quality + freshness +
  // structural SEO). brand_* axes stay daily across self-serve tiers.
  basic: {
    website: { interval: 1, unit: 'months' },
    earned_media: { interval: 1, unit: 'months' },
    brand_narrative: { interval: 1, unit: 'days' },
    brand_authority: { interval: 1, unit: 'days' },
    brand_referral: { interval: 1, unit: 'days' },
  },
  pro: {
    website: { interval: 1, unit: 'weeks' },
    earned_media: { interval: 1, unit: 'weeks' },
    brand_narrative: { interval: 1, unit: 'days' },
    brand_authority: { interval: 1, unit: 'days' },
    brand_referral: { interval: 1, unit: 'days' },
  },
  elite: {
    website: { interval: 1, unit: 'weeks' },
    earned_media: { interval: 1, unit: 'weeks' },
    brand_narrative: { interval: 1, unit: 'days' },
    brand_authority: { interval: 1, unit: 'days' },
    brand_referral: { interval: 1, unit: 'days' },
  },
  enterprise: {
    website: { interval: 1, unit: 'weeks' },
    earned_media: { interval: 1, unit: 'days' },
    brand_narrative: { interval: 1, unit: 'days' },
    brand_authority: { interval: 1, unit: 'days' },
    brand_referral: { interval: 1, unit: 'days' },
  },
  // Agency brands have NO default schedule — agencies drive runs manually
  // (run-now is agency-only) and configure cadence per brand×axis themselves
  // when they want automation. A default schedule would auto-spend agency
  // credits the agency didn't ask for. `'off'` means syncBrand creates no
  // EventBridge schedule; an agency can still opt in via PATCH audit-config.
  agency: {
    website: 'off',
    earned_media: 'off',
    brand_narrative: 'off',
    brand_authority: 'off',
    brand_referral: 'off',
  },
};

/**
 * Whether a tier may CONFIGURE per-brand cadence. Self-serve (basic/pro/elite)
 * is LOCKED to CADENCE_DEFAULTS — only enterprise + agency can override per
 * brand×axis. Single source of truth for the read-only CadenceEditor + the
 * server-side guard on PATCH /brands/:id/audit-config. See pricing plan §2.2.
 */
export function isCadenceConfigurable(tier: string): boolean {
  return tier === 'enterprise' || tier === 'agency';
}

/**
 * Minimum cadence allowed per tier. Protects Bedrock RPM by preventing
 * sub-day cadences until the agency tier ships.
 *
 * If we ever add `unit: 'hours'`, the agency floor would land here as
 * something like `{ interval: 1, unit: 'hours' }`.
 */
export const CADENCE_FLOOR: Record<string, Cadence> = {
  basic: { interval: 1, unit: 'days' },
  pro: { interval: 1, unit: 'days' },
  elite: { interval: 1, unit: 'days' },
  enterprise: { interval: 1, unit: 'days' },
  agency: { interval: 1, unit: 'days' },
};

/** Convert a Cadence to a human-readable French label (UI-facing). */
export function describeCadence(c: Cadence): string {
  if (c === 'off') return 'Désactivé';
  const { interval, unit } = c;
  const singular = { days: 'jour', weeks: 'semaine', months: 'mois' }[unit];
  const plural = { days: 'jours', weeks: 'semaines', months: 'mois' }[unit];
  if (interval === 1) {
    return unit === 'months' ? 'Tous les mois' : `Tous les ${singular}s`;
  }
  return `Tous les ${interval} ${plural}`;
}

/**
 * Resolve effective cadence: brand override → tier default. Returns `'off'`
 * when nothing applies (defensive — every supported tier has every axis
 * covered in `CADENCE_DEFAULTS`).
 */
export function resolveCadence(
  cadenceOverride: Partial<Record<SchedulableAuditType, Cadence>> | undefined,
  tier: string,
  axis: SchedulableAuditType,
): Cadence {
  if (cadenceOverride && axis in cadenceOverride) {
    return cadenceOverride[axis] as Cadence;
  }
  return CADENCE_DEFAULTS[tier]?.[axis] ?? 'off';
}

/**
 * Measurement cadence (Product module "Visibilité GEO") default: not a
 * `SchedulableAuditType` (it never goes through the Trigger Lambda — the
 * schedule invokes `MeasureRunService.start` directly), so it has its own
 * tier default, NOT a lookup into `CADENCE_DEFAULTS`. Non-agency runs daily
 * (paid for via the shared `prompts` gauge already); agency defaults to
 * `'off'` (on-demand) and may opt into a cadence per audience.
 */
export function resolveMeasureCadence(tier: string, audience: { measureCadence?: Cadence }): Cadence {
  if (audience.measureCadence) return audience.measureCadence;
  return tier === 'agency' ? 'off' : { interval: 1, unit: 'days' };
}

function cadenceToDays(c: Exclude<Cadence, 'off'>): number {
  const mult = c.unit === 'days' ? 1 : c.unit === 'weeks' ? 7 : 30;
  return c.interval * mult;
}

/** Cadence period in milliseconds (matches the `rate(N days)` expression). */
export function cadenceIntervalMs(c: Exclude<Cadence, 'off'>): number {
  return cadenceToDays(c) * 24 * 60 * 60 * 1000;
}

/**
 * Next scheduled fire for a materialized schedule, computed locally with NO
 * AWS call — so it scales to any number of brands/axes for free.
 *
 * EventBridge `rate(N days)` with a `StartDate` fires at `firstRunAt`, then
 * every interval after. The next fire is the first such point strictly after
 * `now`. Returns null when the schedule is paused/absent.
 *
 * @param firstRunAt ISO timestamp of the first fire (BrandSchedule.firstRunAt);
 *                   falls back to `createdAt` for legacy schedules written
 *                   before anchoring shipped.
 */
export function nextRunAt(
  cadence: Cadence,
  firstRunAt: string | undefined,
  now: Date = new Date(),
): Date | null {
  if (cadence === 'off' || !firstRunAt) return null;
  const first = new Date(firstRunAt).getTime();
  if (Number.isNaN(first)) return null;
  const periodMs = cadenceIntervalMs(cadence);
  const nowMs = now.getTime();
  if (first > nowMs) return new Date(first); // first fire still pending
  const elapsed = nowMs - first;
  const periodsAhead = Math.floor(elapsed / periodMs) + 1;
  return new Date(first + periodsAhead * periodMs);
}

/**
 * Resolve effective conversation persona cadence: persona-explicit setting → tier default.
 * Pro defaults to weekly, Elite/Enterprise to daily; agency defaults to 'off' (manual runs).
 * Returns 'off' when the tier doesn't schedule conversations automatically.
 */
export function resolveConversationCadence(tier: string, persona: { cadence?: Cadence }): Cadence {
  if (persona.cadence) return persona.cadence;
  if (tier === 'pro') return { interval: 1, unit: 'weeks' };
  if (tier === 'elite' || tier === 'enterprise') return { interval: 1, unit: 'days' };
  return 'off'; // agency drives runs manually by default
}

/**
 * Throws when a cadence is faster than the tier floor. Caller wraps in the
 * appropriate framework exception (`BadRequestException` server-side).
 */
export function assertCadenceAllowed(tier: string, cadence: Cadence): void {
  if (cadence === 'off') return;
  const floor = CADENCE_FLOOR[tier];
  if (!floor || floor === 'off') return;
  if (cadenceToDays(cadence) < cadenceToDays(floor)) {
    throw new Error(
      `Cadence ${describeCadence(cadence)} is faster than tier ${tier} floor ${describeCadence(floor)}`,
    );
  }
}
